import React, { Component } from 'react';
import { observer } from 'mobx-react';
import { Box } from 'core/components';
import UtilizationChart from 'app/components/cloudpakFlowIndicators/UtilizationChart';
import CloudPakFlowMetricsWrapper from 'app/components/cloudpakFlowIndicators/CloudPakFlowMetricsWrapper';

@observer
export default class UniversalCloudPakCard extends Component {
  state = {
    selectedTimePeriod: '1_day', // Default to 1 day
    selectedMetric: 'peak', // Default to peak metric
    selectedPointData: null // For storing selected chart point data
  };

  handleTimePeriodChange = (selectedOption) => {
    if (selectedOption && selectedOption.value) {
      this.setState({
        selectedTimePeriod: selectedOption.value,
        selectedPointData: null // Clear selected point when time period changes
      });
    }
  };

  handleMetricSelection = (metricValue) => {
    this.setState({
      selectedMetric: metricValue,
      selectedPointData: null // Clear selected point when switching metrics
    });
  };

  handleSelectedPointChange = (selectedData) => {
    // Clear previous selection and set new data in one state update
    this.setState({ selectedPointData: selectedData });
  };

  // Extract the rawData array from the flow data model
  getModelDataArray(flowDataModel, metricName = 'flows_discovered') {
    if (!flowDataModel || !flowDataModel.models) {
      return null;
    }
    const model = flowDataModel?.models.find((item) => {
      const metricsArr = item.get('metrics');
      if (metricsArr[0] === metricName) {
        return item;
      }
      return null;
    });
    if (model) {
      return model.get('timeseries');
    }
    return null;
  }

  /**
   * Get the peak received flow from the received flow data.
   */
  getPeakReceived(receivedFlowArray) {
    // Find the array item with the largest flow value
    if (receivedFlowArray && Array.isArray(receivedFlowArray) && receivedFlowArray.length > 0) {
      const peakFlowObj = receivedFlowArray.reduce((maxItem, currentItem) =>
        // Each item is [timestamp, flow_value]
        // Compare flow values (index 1)
        currentItem[1] > maxItem[1] ? currentItem : maxItem
      );

      const [peakFlowTimestamp, peakReceivedFlowValue] = peakFlowObj;
      return {
        peakFlowTimestamp,
        peakReceivedFlowValue,
        peakFlowObj
      };
    }
    return null;
  }

  /**
   * Using same timestamp from peak received flow, get corresponding raw and stored flow values.
   */
  getPeakRawAndStored(peakFlowTimestamp, rawFlowArray, storedFlowArray) {
    // Find matching timestamp in raw flow data
    const rawFlowItem = rawFlowArray?.find((item) => item[0] === peakFlowTimestamp);
    const peakRawFlowValue = rawFlowItem ? rawFlowItem[1] : null;

    // Find matching timestamp in stored flow data
    const storedFlowItem = storedFlowArray?.find((item) => item[0] === peakFlowTimestamp);
    const peakStoredFlowValue = storedFlowItem ? storedFlowItem[1] : null;

    return {
      peakRawFlowValue,
      peakStoredFlowValue,
      rawFlowItem,
      storedFlowItem
    };
  }

  /**
   * Get the latest flow values from all three flow arrays.
   * Only returns values if all arrays have a common latest timestamp.
   */
  getLatest(rawFlowArray, receivedFlowArray, storedFlowArray) {
    if (
      !rawFlowArray ||
      !receivedFlowArray ||
      !storedFlowArray ||
      !Array.isArray(rawFlowArray) ||
      !Array.isArray(receivedFlowArray) ||
      !Array.isArray(storedFlowArray) ||
      rawFlowArray.length === 0 ||
      receivedFlowArray.length === 0 ||
      storedFlowArray.length === 0
    ) {
      return null;
    }

    // Get the last timestamp from each array
    const lastRawTimestamp = rawFlowArray[rawFlowArray.length - 1][0];
    const lastReceivedTimestamp = receivedFlowArray[receivedFlowArray.length - 1][0];
    const lastStoredTimestamp = storedFlowArray[storedFlowArray.length - 1][0];

    // Check if all arrays have the same latest timestamp
    if (lastRawTimestamp === lastReceivedTimestamp && lastReceivedTimestamp === lastStoredTimestamp) {
      const latestRawFlowValue = rawFlowArray[rawFlowArray.length - 1][1];
      const latestReceivedFlowValue = receivedFlowArray[receivedFlowArray.length - 1][1];
      const latestStoredFlowValue = storedFlowArray[storedFlowArray.length - 1][1];

      return {
        latestFlowTimestamp: lastRawTimestamp,
        latestRawFlowValue,
        latestReceivedFlowValue,
        latestStoredFlowValue
      };
    }

    // If timestamps don't match, find the latest common timestamp
    const minLatestTimestamp = Math.min(lastRawTimestamp, lastReceivedTimestamp, lastStoredTimestamp);

    // Find values for the common timestamp
    const rawFlowItem = rawFlowArray.find((item) => item[0] === minLatestTimestamp);
    const receivedFlowItem = receivedFlowArray.find((item) => item[0] === minLatestTimestamp);
    const storedFlowItem = storedFlowArray.find((item) => item[0] === minLatestTimestamp);

    if (rawFlowItem && receivedFlowItem && storedFlowItem) {
      return {
        latestFlowTimestamp: minLatestTimestamp,
        latestRawFlowValue: rawFlowItem[1],
        latestReceivedFlowValue: receivedFlowItem[1],
        latestStoredFlowValue: storedFlowItem[1]
      };
    }

    return null;
  }

  getDataResolution(peakReceivedFlowValue, peakStoredFlowValue) {
    // Determine data resolution based on the ratio
    let dataResolution = null;
    if (peakReceivedFlowValue && peakStoredFlowValue) {
      const ratio = peakReceivedFlowValue / peakStoredFlowValue;
      if (ratio <= 1) {
        dataResolution = 'ok';
      } else if (ratio < 2) {
        dataResolution = 'downsampled';
      } else {
        dataResolution = 'low';
      }
    }
    return dataResolution;
  }

  render() {
    const { pak_fps, max_fps, id: plan_id, onAlertDataChange } = this.props;
    // note that max_fps is passed from PlanCard as a prop, should always be same value as pak_fps for Universal CloudPaks
    const { selectedTimePeriod, selectedMetric, selectedPointData } = this.state;

    // Create a render function that has access to the current scope
    const renderUtilizationChart = ({ loading, rawFlowModel, receivedFlowModel, storedFlowModel, error }) => {
      const rawFlowArray = this.getModelDataArray(rawFlowModel, 'in');
      const receivedFlowArray = this.getModelDataArray(receivedFlowModel, 'in');
      const storedFlowArray = this.getModelDataArray(storedFlowModel, 'out');

      let flowTimestamp;
      let rawFlowValue;
      let receivedFlowValue;
      let storedFlowValue;
      let avgConfiguredSamplingRate;

      if (selectedMetric === 'peak') {
        // Use existing peak logic
        const { peakFlowTimestamp, peakReceivedFlowValue } = this.getPeakReceived(receivedFlowArray) || {};
        const { peakRawFlowValue, peakStoredFlowValue } = peakFlowTimestamp
          ? this.getPeakRawAndStored(peakFlowTimestamp, rawFlowArray, storedFlowArray)
          : {};
        flowTimestamp = peakFlowTimestamp;
        rawFlowValue = peakRawFlowValue;
        receivedFlowValue = peakReceivedFlowValue;
        storedFlowValue = peakStoredFlowValue;
        avgConfiguredSamplingRate =
          peakRawFlowValue && peakReceivedFlowValue
            ? `1:${(peakRawFlowValue / peakReceivedFlowValue).toFixed(1)}`
            : null;
      } else if (selectedMetric === 'latest') {
        // Use new latest logic
        const latestData = this.getLatest(rawFlowArray, receivedFlowArray, storedFlowArray);
        if (latestData) {
          const { latestFlowTimestamp, latestRawFlowValue, latestReceivedFlowValue, latestStoredFlowValue } =
            latestData;
          flowTimestamp = latestFlowTimestamp;
          rawFlowValue = latestRawFlowValue;
          receivedFlowValue = latestReceivedFlowValue;
          storedFlowValue = latestStoredFlowValue;
          avgConfiguredSamplingRate =
            latestRawFlowValue && latestReceivedFlowValue
              ? `1:${(latestRawFlowValue / latestReceivedFlowValue).toFixed(1)}`
              : null;
        }
      } else if (selectedMetric === 'selected') {
        // Use selected point data if available
        if (selectedPointData) {
          const { actualTimestamp, selectedRawFlowValue, selectedReceivedFlowValue, selectedStoredFlowValue } =
            selectedPointData;
          flowTimestamp = actualTimestamp;
          rawFlowValue = selectedRawFlowValue;
          receivedFlowValue = selectedReceivedFlowValue;
          storedFlowValue = selectedStoredFlowValue;
          avgConfiguredSamplingRate =
            selectedRawFlowValue && selectedReceivedFlowValue
              ? `1:${(selectedRawFlowValue / selectedReceivedFlowValue).toFixed(1)}`
              : null;
        } else {
          // No point selected yet
          flowTimestamp = null;
          rawFlowValue = null;
          receivedFlowValue = null;
          storedFlowValue = null;
          avgConfiguredSamplingRate = null;
        }
      }

      const dataResolution = this.getDataResolution(receivedFlowValue, storedFlowValue);

      // Pass alert data to parent component
      if (onAlertDataChange) {
        onAlertDataChange({
          avgConfiguredSamplingRate,
          dataResolution
        });
      }

      return (
        <UtilizationChart
          rawFlowArray={rawFlowArray}
          receivedFlowArray={receivedFlowArray}
          storedFlowArray={storedFlowArray}
          rawFlowValue={rawFlowValue}
          receivedFlowValue={receivedFlowValue}
          storedFlowValue={storedFlowValue}
          flowValueTimestamp={flowTimestamp}
          planMaxFps={max_fps}
          avgConfiguredSamplingRate={avgConfiguredSamplingRate}
          dataResolution={dataResolution}
          loading={loading}
          error={error}
          pak_fps={pak_fps}
          selectedTimePeriod={selectedTimePeriod}
          selectedMetric={selectedMetric}
          selectedPointData={selectedPointData}
          onTimePeriodChange={this.handleTimePeriodChange}
          onMetricSelection={this.handleMetricSelection}
          onSelectedPointChange={this.handleSelectedPointChange}
        />
      );
    };

    return (
      <Box mt={3} width="100%">
        <CloudPakFlowMetricsWrapper lookback={selectedTimePeriod} plan_id={plan_id}>
          {renderUtilizationChart}
        </CloudPakFlowMetricsWrapper>
      </Box>
    );
  }
}
